import { PlxsButton, PlxsHeading } from "@plexlogic/plexus-ui";
import React from "react";

interface ErrorFallbackProps {
  error: unknown;
  resetErrorBoundary: () => void;
}

const formatError = (e: unknown): string => {
  if (e instanceof Error) return e.stack ?? e.message;
  if (typeof e === "string") return e;
  try {
    return JSON.stringify(e, null, 2);
  } catch {
    return String(e);
  }
};

/**
 * Reusable Error Fallback component for ErrorBoundary
 * Displays a user-friendly error message with recovery options
 */
const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetErrorBoundary,
}) => {
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error);
  }

  return (
    <div
      style={{
        padding: "2rem",
        maxWidth: "600px",
        margin: "2rem auto",
        textAlign: "center",
        border: "1px solid #ef4444",
        borderRadius: "8px",
        backgroundColor: "#fef2f2",
      }}
    >
      <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>⚠️</div>
      <PlxsHeading element="h2" align="center" color="urgent">
        Something went wrong
      </PlxsHeading>
      <p style={{ marginBottom: "1.5rem", color: "#7f1d1d", lineHeight: 1.5 }}>
        Please try again later. If the problem persists, contact support.
      </p>

      {/* Show error details in development */}
      {process.env.NODE_ENV === "development" && (
        <details
          style={{
            marginBottom: "1.5rem",
            textAlign: "left",
            fontSize: "0.875rem",
          }}
        >
          <summary style={{ cursor: "pointer", marginBottom: "0.5rem" }}>
            Error Details (Dev Only)
          </summary>
          <pre
            style={{
              backgroundColor: "#f5f5f5",
              padding: "0.5rem",
              borderRadius: "4px",
              overflow: "auto",
              fontSize: "0.75rem",
            }}
          >
            {formatError(error)}
          </pre>
        </details>
      )}

      <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center" }}>
        <PlxsButton
          dataTestId="error-fallback-retry-button"
          onClick={resetErrorBoundary}
          variant="primary"
          tone="urgent"
          label="Try Again"
        />
        <PlxsButton
          dataTestId="error-fallback-refresh-button"
          onClick={() => window.location.reload()}
          variant="secondary"
          tone="brand"
          label="Refresh Page"
        />
      </div>
    </div>
  );
};

export default ErrorFallback;
